/**
 * FmFigure — paired figure + explanation layout component.
 *
 * Renders a chart/figure slot alongside a mandatory explanation region.
 * The explanation region is ALWAYS rendered (never hidden), enforcing
 * the design-system rule that bare charts cannot ship without context.
 *
 * State slots: loading (Skeleton), error (Alert), empty (Empty), figure (pass-through).
 *
 * Layout: stacked (mobile < 769px) → side-by-side (desktop ≥ 769px) via CSS only.
 */
import React from 'react';
import { FmEmpty, FmSkeleton } from './FmFeedback';
import { FmBanner } from './FmBanner';
import { shouldStackForTier } from './FmResponsive';
import type { FmDeviceTier } from './FmResponsive';
import { headingLabelText, useHostedHeading } from './fmHostedHeading';
import './FmFigure.css';

export interface FmFigureProps {
  title: string;
  /**
   * The surrounding surface ALREADY printed this title, so the figure renders
   * captionless. Only needed where no published host can
   * reach the conclusion: a titled host that is not an FmSectionBand, or a
   * ReactNode title with no comparable plain text.
   */
  hosted?: boolean;
  figure?: React.ReactNode;
  explanation?: React.ReactNode;
  explanationTitle?: string;
  loading?: boolean;
  error?: string | null;
  emptyText?: string;
  emptyExplanationText?: string;
  source?: React.ReactNode;
  /**
   * Device tier (FmResponsive contract). 'tablet' and 'mobile' add
   * fm-figure--stack which forces the figure/explanation body to a single
   * column (explanation drops below the chart), overriding the ≥48em row.
   */
  tier?: FmDeviceTier;
  /**
   * Explicit layout override for narrow graphic slots embedded inside another
   * split section. Defaults to responsive auto behavior.
   */
  layout?: 'auto' | 'stack';
  /**
   * @internal DS/migration class hook; forbidden for route-owned layout, colour,
   * spacing, and figure chrome. Add a named variant instead.
   */
  className?: string;
  'data-testid'?: string;
}

const cx = (...parts: Array<string | false | null | undefined>): string =>
  parts.filter(Boolean).join(' ');

export const FmFigure: React.FC<FmFigureProps> = ({
  title,
  hosted,
  figure,
  explanation,
  explanationTitle,
  loading = false,
  error,
  emptyText,
  emptyExplanationText,
  source,
  tier,
  layout = 'auto',
  className,
  'data-testid': dataTestId,
}) => {
  const tierStacks = tier !== undefined && shouldStackForTier(tier);
  const shouldStack = layout === 'stack' || tierStacks;
  // One section = one title: a titled host already printed this caption, so the
  // figure drops it rather than printing it twice. The
  // figure caption is not a heading tag, so only `render` applies here.
  const heading = useHostedHeading(title, 3, hosted);
  const renderFigureSlot = () => {
    if (loading) {
      return <FmSkeleton rows={4} />;
    }
    if (error) {
      return <FmBanner tone="error" title={error} />;
    }
    if (!figure && emptyText) {
      return <FmEmpty description={emptyText} />;
    }
    return figure;
  };

  const renderExplanationRegion = () => {
    if (!explanation) {
      return (
        <div
          className="fm-figure__explanation-empty"
          data-testid="fm-figure-explanation-empty"
        >
          {emptyExplanationText ?? null}
        </div>
      );
    }

    return (
      <>
        {explanationTitle ? (
          <p className="fm-figure__explanation-title">{explanationTitle}</p>
        ) : null}
        <div className="fm-figure__explanation-body">{explanation}</div>
        {source ? (
          <div className="fm-figure__source">{source}</div>
        ) : null}
      </>
    );
  };

  return (
    <section
      className={cx('fm-figure', shouldStack && 'fm-figure--stack', className)}
      // Titleless still names the region: dropping a DUPLICATE title must not
      // cost a screen reader the figure's name.
      aria-label={heading.render ? undefined : headingLabelText(title)}
      data-testid={dataTestId}
    >
      {heading.render ? (
        <div className="fm-figure__head">
          <p className="fm-figure__title">{title}</p>
        </div>
      ) : null}
      <div className="fm-figure__body">
        <div className="fm-figure__chart">{renderFigureSlot()}</div>
        <div
          className="fm-figure__explanation"
          data-testid="fm-figure-explanation"
        >
          {renderExplanationRegion()}
        </div>
      </div>
    </section>
  );
};

export default FmFigure;
